import type { ReceiptUploadConfig } from "../components/UploadReceiptDialog/useReceiptUploadConfig";

export const UPLOAD_CONCURRENCY = 3;

export type UploadItemStatus =
  | "queued"
  | "uploading"
  | "scanning"
  | "added_pending"
  | "added_unmatched"
  | "failed"
  | "stalled";

export type ResolvedItemStatus = "added_pending" | "added_unmatched";

export type UploadItem = {
  id: string;
  file: File;
  status: UploadItemStatus;
  receiptId?: string;
  errorCode?: string;
  retryable: boolean;
  retryAfterSeconds?: number;
  resolvedStatus?: string;
};

export type UploadSessionState = {
  items: UploadItem[];
  nextItemNumber: number;
  dismissed: boolean;
  recentlyResolvedIds: string[];
  // Bumped on add, accepted and resolved so the poll hook can reset its idle clock.
  activityCount: number;
};

export type UploadSessionPhase = "idle" | "in_progress" | "done";

export type UploadSessionCounters = {
  total: number;
  resolved: number;
  pending: number;
  unmatched: number;
  attention: number;
  unsent: number;
  scanning: number;
  stalled: number;
  phase: UploadSessionPhase;
};

export type UploadSessionAction =
  | { type: "add"; files: File[]; config: ReceiptUploadConfig }
  | { type: "start"; id: string }
  | { type: "accepted"; id: string; receiptId: string }
  | { type: "resolved"; receiptId: string; receiptStatus: string }
  | {
      type: "failed";
      id: string;
      errorCode?: string;
      retryable: boolean;
      retryAfterSeconds?: number;
    }
  | { type: "throttled"; id: string; retryAfterSeconds: number }
  | { type: "cancel"; id: string }
  | { type: "remove"; id: string }
  | { type: "retry"; id: string }
  | { type: "abortUnsent" }
  | { type: "stalled" }
  | { type: "dismissProgress" };

export const INITIAL_UPLOAD_SESSION_STATE: UploadSessionState = {
  items: [],
  nextItemNumber: 1,
  dismissed: false,
  recentlyResolvedIds: [],
  activityCount: 0,
};

const RESOLVED_STATUS_BY_RECEIPT_STATUS: Record<string, ResolvedItemStatus> = {
  awaiting_review: "added_pending",
  confirmed: "added_pending",
  no_match: "added_unmatched",
  extraction_failed: "added_unmatched",
  rejected: "added_unmatched",
  orphaned: "added_unmatched",
};

export const resolvedStatusForReceiptStatus = (
  receiptStatus: string
): ResolvedItemStatus | undefined =>
  RESOLVED_STATUS_BY_RECEIPT_STATUS[receiptStatus];

const UNSENT_STATUSES: ReadonlySet<UploadItemStatus> = new Set([
  "queued",
  "uploading",
]);

const isUnsent = (item: UploadItem) => UNSENT_STATUSES.has(item.status);

const REMOVABLE_STATUSES: ReadonlySet<UploadItemStatus> = new Set([
  "added_pending",
  "added_unmatched",
  "failed",
  "stalled",
]);

const clientCheckFailure = (
  file: File,
  config: ReceiptUploadConfig
): string | undefined => {
  if (!config.allowed_mime_types.includes(file.type)) {
    return "unsupported_mime_type";
  }
  if (file.size > config.max_size_bytes) return "file_too_large";
  return undefined;
};

const updateItem = (
  state: UploadSessionState,
  id: string,
  update: (item: UploadItem) => UploadItem
): UploadSessionState => ({
  ...state,
  items: state.items.map((item) => (item.id === id ? update(item) : item)),
});

const removeItems = (
  state: UploadSessionState,
  shouldRemove: (item: UploadItem) => boolean
): UploadSessionState => ({
  ...state,
  items: state.items.filter((item) => !shouldRemove(item)),
});

const failItem = (
  item: UploadItem,
  errorCode: string | undefined,
  retryable: boolean,
  retryAfterSeconds?: number
): UploadItem => ({
  ...item,
  status: "failed",
  errorCode,
  retryable,
  retryAfterSeconds,
});

export const uploadSessionReducer = (
  state: UploadSessionState,
  action: UploadSessionAction
): UploadSessionState => {
  switch (action.type) {
    case "add": {
      let number = state.nextItemNumber;
      const added = action.files.map((file): UploadItem => {
        const id = `upload-${number}`;
        number += 1;
        const errorCode = clientCheckFailure(file, action.config);
        return errorCode
          ? {
              id,
              file,
              status: "failed",
              errorCode,
              retryable: false,
            }
          : { id, file, status: "queued", retryable: false };
      });
      return {
        ...state,
        items: [...state.items, ...added],
        nextItemNumber: number,
        dismissed: false,
        activityCount: state.activityCount + 1,
      };
    }
    case "start":
      return updateItem(state, action.id, (item) =>
        item.status === "queued" ? { ...item, status: "uploading" } : item
      );
    case "accepted": {
      const next = updateItem(state, action.id, (item) =>
        item.status === "uploading"
          ? {
              ...item,
              status: "scanning",
              receiptId: action.receiptId,
            }
          : item
      );
      return next === state
        ? state
        : { ...next, activityCount: state.activityCount + 1 };
    }
    case "resolved": {
      const resolvedStatus = resolvedStatusForReceiptStatus(
        action.receiptStatus
      );
      const target = state.items.find(
        (item) =>
          item.status === "scanning" && item.receiptId === action.receiptId
      );
      if (!resolvedStatus || !target) return state;
      return {
        ...updateItem(state, target.id, (item) => ({
          ...item,
          status: resolvedStatus,
          resolvedStatus: action.receiptStatus,
        })),
        recentlyResolvedIds: [...state.recentlyResolvedIds, action.receiptId],
        activityCount: state.activityCount + 1,
      };
    }
    case "failed":
      return updateItem(state, action.id, (item) =>
        isUnsent(item)
          ? failItem(
              item,
              action.errorCode,
              action.retryable,
              action.retryAfterSeconds
            )
          : item
      );
    case "throttled":
      return {
        ...state,
        items: state.items.map((item) =>
          (item.id === action.id && isUnsent(item)) || item.status === "queued"
            ? failItem(item, "throttled", true, action.retryAfterSeconds)
            : item
        ),
      };
    case "cancel":
      return removeItems(
        state,
        (item) =>
          item.id === action.id &&
          (isUnsent(item) || item.status === "scanning")
      );
    case "remove":
      return removeItems(
        state,
        (item) => item.id === action.id && REMOVABLE_STATUSES.has(item.status)
      );
    case "stalled":
      return {
        ...state,
        items: state.items.map((item) =>
          item.status === "scanning" ? { ...item, status: "stalled" } : item
        ),
      };
    case "retry": {
      const canRetry = state.items.some(
        (item) =>
          item.id === action.id && item.status === "failed" && item.retryable
      );
      if (!canRetry) return state;
      return {
        ...updateItem(state, action.id, (item) => ({
          ...item,
          status: "queued",
          errorCode: undefined,
          retryAfterSeconds: undefined,
        })),
        dismissed: false,
      };
    }
    case "abortUnsent":
      return removeItems(state, isUnsent);
    case "dismissProgress":
      return { ...state, dismissed: true };
    default:
      return state;
  }
};

export const selectCounters = (
  state: UploadSessionState
): UploadSessionCounters => {
  const counts = {
    pending: 0,
    unmatched: 0,
    attention: 0,
    unsent: 0,
    scanning: 0,
    stalled: 0,
  };
  state.items.forEach((item) => {
    switch (item.status) {
      case "added_pending":
        counts.pending += 1;
        break;
      case "added_unmatched":
        counts.unmatched += 1;
        break;
      case "failed":
        counts.attention += 1;
        break;
      case "scanning":
        counts.scanning += 1;
        break;
      case "stalled":
        counts.stalled += 1;
        break;
      default:
        counts.unsent += 1;
    }
  });
  const total = state.items.length;
  let phase: UploadSessionPhase = "done";
  if (total === 0) phase = "idle";
  else if (counts.unsent > 0 || counts.scanning > 0) phase = "in_progress";
  return {
    total,
    resolved: counts.pending + counts.unmatched,
    ...counts,
    phase,
  };
};
